import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/api/api';
import { deepClone, forceNull, isNullOrUndefined } from 'app/shared/lib/utilities';
import { ICustomFieldTargetColumnsContainer, ICustomFieldTargetColumnsSettings } from 'app/shared/lib/custom-field-definitions';
import { CustomFieldContext } from 'app/core/lib/custom-field-context';
import { IPopup } from 'app/shared/elements/popup-base';
import { Toaster } from 'app/shared/lib/toaster';
import { EpmpManager } from 'app/ext/epmp/epmp-manager';
import * as CD from 'app/cd/models/essette-api';

@autoinject()
export class MemberM360Information extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public memberId: string | null = null;

    protected member: CD.Member | null = null;

    protected isNullOrUndefined = isNullOrUndefined;
    protected customFieldTargetColumns: ICustomFieldTargetColumnsContainer | null = null;

    protected canSaveMemberDemographics: boolean = false;
    protected saveMemberDemographics: CD.MemberDemographics = {};
    protected showEPMP: boolean | null | undefined = false;
    protected validEPMPGroupNumber: string | null = null;

    ////****************** Element References ***************************************////
    private $editMemberDemographicsModal!: IPopup;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly customFieldContext: CustomFieldContext,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        protected epmpManager: EpmpManager,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.checkPermissions();
        this.loadCustomFieldTargets();
        this.reload();
    }

    ////****************** Events ***************************************************////
    public async reload(): Promise<void> {
        await this.loadMember();
        this.showEPMPButtons();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.canSaveMemberDemographics = !!this.accountContext.hasPermission('CD.MemberDemographics.Save');
    }

    protected openEditMemberDemographics(): void {
        //Set each object to their respective values for editing demographics
        if(this.member?.demographics) {
            this.saveMemberDemographics = deepClone(this.member.demographics);
            if(this.saveMemberDemographics.hasAdvanceDirective === null) {
                this.saveMemberDemographics.hasAdvanceDirective = false;
            }
            if(this.saveMemberDemographics.hasDurablePowerOfAttorney === null) {
                this.saveMemberDemographics.hasDurablePowerOfAttorney = false;
            }
        }
        else {
            this.saveMemberDemographics = { hasDurablePowerOfAttorney: false, hasAdvanceDirective: false };
        }
        this.$editMemberDemographicsModal.open();
    }

    protected async editMemberDemographicsSuccess() {
        if(!!this.$editMemberDemographicsModal) {
            const isSaved = await this.saveDemographics();

            if(isSaved) {
                // Reload Member Information
                await this.reload();

                // Close edit member demographics modal
                this.$editMemberDemographicsModal.close();
            }
        }
    }

    protected buildMemberDemographics(memberDemographics: CD.MemberDemographics): CD.MemberDemographics {
        const saveMemberDemographics = {
            member: { id: this.memberId },
            pronouns: forceNull(memberDemographics.pronouns),
            preferredName: forceNull(memberDemographics.preferredName),
            gender: memberDemographics.gender ? { code: memberDemographics.gender.code } : null,
            race: memberDemographics.race ? { code: memberDemographics.race.code } : null,
            disability: forceNull(memberDemographics.disability),
            hasDurablePowerOfAttorney: memberDemographics.hasDurablePowerOfAttorney,
            hasAdvanceDirective: memberDemographics.hasAdvanceDirective,
            advanceDirectiveExpirationDate: memberDemographics.advanceDirectiveExpirationDate
        };
        return saveMemberDemographics;
    }

    protected showEPMPButtons(): void {
        const showEPMP = this.env.config.pageConfigurations.cd.member.summary.showEPMP;
        const memberIdentifier = this.member?.identifier?.primary;
        const memberEligibility = this.member?.history?.eligibility;

        if(!showEPMP || !memberIdentifier || !memberEligibility) { return }

        try {
            this.validEPMPGroupNumber = this.epmpManager.getValidGroupNumber(memberIdentifier, memberEligibility);

            if(this.validEPMPGroupNumber) {
                this.showEPMP = true;
            }
        } catch(error) {
            console.error(this.i18n.tr('ext:epmp.local.buttonDisplayError'), error);
        }
    }

    protected openEPMP(variation: 'Contacts' | 'Preferences'): void {
        const memberIdentifier = this.member?.identifier?.primary;
        const groupNumber = this.validEPMPGroupNumber;

        if(memberIdentifier && groupNumber) {
            this.epmpManager.open(memberIdentifier, groupNumber, variation);
        } else {
            this.toaster.error().message(this.i18n.tr('ext:epmp.local.openWindowError')).show();
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadCustomFieldTargets(): Promise<void> {
        const settings: ICustomFieldTargetColumnsSettings = {
            definitionCount: 25,
            filterInactive: true,
            sortByLabel: true
        };
        this.customFieldTargetColumns = await this.customFieldContext.getCustomFieldTargetColumns('CD.Member', settings);
    }

    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;

        const includes = [
            'address.home',
            'address.mailing',
            'address.work',
            'demographics',
            'demographics.gender',
            'demographics.race',
            'contact.preference',
            'custom',
            'demographics',
            'demographics.gender',
            'demographics.race',
            'ethnicity',
            'gender',
            'history.eligibility',
            'language.reads',
            'language.speaks',
            'maritalStatus',
            'phoneNumber.cell',
            'phoneNumber.fax',
            'phoneNumber.home',
            'phoneNumber.work',
            'race'
        ];

        const fields = [
            'address.home.city',
            'address.home.postalCode',
            'address.home.region.code',
            'address.home.street1',
            'address.home.street2',
            'address.mailing.city',
            'address.mailing.postalCode',
            'address.mailing.region.code',
            'address.mailing.street1',
            'address.mailing.street2',
            'address.work.city',
            'address.work.postalCode',
            'address.work.region.code',
            'address.work.street1',
            'address.work.street2',
            'age.*',
            'birthDate',
            'contact.preference.summary',
            'custom.lookup.*',
            'custom.text.*',
            'deathDate',
            'demographics.advanceDirectiveExpirationDate',
            'demographics.disability',
            'demographics.gender.description',
            'demographics.hasAdvanceDirective',
            'demographics.hasDurablePowerOfAttorney',
            'demographics.preferredName',
            'demographics.pronouns',
            'demographics.race.description',
            'description',
            'emailAddress',
            'ethnicity.description',
            'gender.description',
            'history.eligibility.groupNumber',
            'identifier.medicaid',
            'identifier.primary',
            'identifier.socialSecurityNumber',
            'language.reads.description',
            'language.speaks.description',
            'maritalStatus.description',
            'name.full',
            'phoneNumber.cell.extension',
            'phoneNumber.cell.number',
            'phoneNumber.cell.country.phoneNumber.callingCode',
            'phoneNumber.fax.extension',
            'phoneNumber.fax.number',
            'phoneNumber.fax.country.phoneNumber.callingCode',
            'phoneNumber.home.extension',
            'phoneNumber.home.number',
            'phoneNumber.home.country.phoneNumber.callingCode',
            'phoneNumber.work.extension',
            'phoneNumber.work.number',
            'phoneNumber.work.country.phoneNumber.callingCode',
            'race.description',
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMember(this.memberId, includes, fields);
            this.member = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveDemographics(): Promise<boolean> {
        this.state.beginSave();
        let isSuccessful = false;
        const memberDemographics = this.buildMemberDemographics(this.saveMemberDemographics);

        try {
            const response = await this.memberService.saveMemberDemographics(memberDemographics);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.updated('cd:member.demographics.$header', isSuccessful, 2);
            this.state.endSave();
            return isSuccessful;
        }
    }

}
